import { BaseWindow } from './BaseWindow.js';
import { VERSION } from '../version.js';

/**
 * Window that displays git commit history in a scrollable list
 */
export class VersionNotesWindow extends BaseWindow {
  constructor() {
    super({
      id: 'versionNotesWindow',
      title: 'SOULFORGE VERSION HISTORY',
      name: 'Version Notes',
      width: 600,
      height: 500,
      minWidth: 400,
      minHeight: 300,
      onInit: () => this.loadCommitHistory(),
      onShow: () => this.refreshIfNeeded()
    });
    
    this.commits = [];
    this.lastLoadTime = 0;
    this.contentDiv = null;
    this.loadingDiv = null;
    this.errorDiv = null;
    this.expandedCommitIndex = 0; // Only first commit expanded by default
  }
  
  /**
   * Override createTitleBar to add refresh button
   */
  createTitleBar() {
    const titleBar = document.createElement('div');
    titleBar.className = this.titleBarClass || '';
    titleBar.style.cssText = `
      background: rgba(0, 255, 0, 0.1);
      border-bottom: 1px solid #0f0;
      padding: 8px 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      user-select: none;
    `;
    
    titleBar.innerHTML = `
      <span class="window-title" style="display: flex; align-items: center; gap: 15px;">
        <span>VERSION HISTORY</span>
        <span style="color: rgba(0, 255, 0, 0.8); font-size: 11px;">v${VERSION}</span>
      </span>
      <div style="display: flex; gap: 8px; align-items: center;">
        <button id="refreshCommitsBtn" style="
          background: transparent;
          border: 1px solid rgba(0, 255, 0, 0.5);
          color: rgba(0, 255, 0, 0.8);
          padding: 2px 8px;
          cursor: pointer;
          font-size: 14px;
          line-height: 1;
          font-family: 'Berkeley Mono', 'Courier New', monospace;
          transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        " onmouseover="this.style.borderColor='#0f0'; this.style.color='#0f0'; this.style.background='rgba(0, 255, 0, 0.1)';" 
          onmouseout="this.style.borderColor='rgba(0, 255, 0, 0.5)'; this.style.color='rgba(0, 255, 0, 0.8)'; this.style.background='transparent';"
          onmousedown="this.style.transform='scale(0.95)';"
          onmouseup="this.style.transform='scale(1)';">↻</button>
        <button class="window-close-btn" style="
          background: transparent;
          border: 1px solid #0f0;
          color: #0f0;
          padding: 2px 8px;
          cursor: pointer;
          font-size: 14px;
          line-height: 1;
        ">×</button>
      </div>
    `;
    
    // Handle close button
    const closeBtn = titleBar.querySelector('.window-close-btn');
    closeBtn.addEventListener('click', () => this.hide());
    
    // Handle refresh button
    const refreshBtn = titleBar.querySelector('#refreshCommitsBtn');
    refreshBtn.addEventListener('click', () => this.loadCommitHistory(true));
    
    return titleBar;
  }
  
  createContent() {
    const content = document.createElement('div');
    content.style.cssText = `
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.5);
    `;
    
    // Create loading indicator
    this.loadingDiv = document.createElement('div');
    this.loadingDiv.style.cssText = `
      display: none;
      padding: 20px;
      text-align: center;
      color: #0f0;
      font-size: 11px;
    `;
    this.loadingDiv.innerHTML = `
      <div style="margin-bottom: 10px;">LOADING COMMIT HISTORY...</div>
      <div style="font-size: 20px; animation: spin 1s linear infinite;">◈</div>
    `;
    
    // Create error display
    this.errorDiv = document.createElement('div');
    this.errorDiv.style.cssText = `
      display: none;
      padding: 20px;
      text-align: center;
      color: #f00;
      font-size: 11px;
    `;
    
    // Create scrollable content area
    this.contentDiv = document.createElement('div');
    this.contentDiv.style.cssText = `
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0;
      background: rgba(0, 0, 0, 0.3);
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    `;
    
    // Add custom scrollbar styling
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      #versionNotesWindow .commit-item {
        border-bottom: 1px solid rgba(0, 255, 0, 0.2);
        transition: background 0.2s;
      }
      
      #versionNotesWindow .commit-item:hover {
        background: rgba(0, 255, 0, 0.05);
      }
      
      #versionNotesWindow .commit-header {
        padding: 12px 15px;
        cursor: pointer;
        user-select: none;
      }
      
      #versionNotesWindow .commit-header:hover {
        background: rgba(0, 255, 0, 0.08);
      }
      
      #versionNotesWindow .commit-header.expanded {
        background: rgba(0, 255, 0, 0.1);
        border-bottom: 1px solid rgba(0, 255, 0, 0.3);
      }
      
      #versionNotesWindow .commit-details {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-out;
        background: rgba(0, 0, 0, 0.3);
        opacity: 0;
      }
      
      #versionNotesWindow .commit-details.expanded {
        max-height: 500px;
        transition: max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-out;
        opacity: 1;
      }
      
      #versionNotesWindow .commit-details-content {
        padding: 15px 20px;
        font-size: 11px;
        line-height: 1.6;
        color: rgba(0, 255, 0, 0.9);
      }
      
      #versionNotesWindow .expand-indicator {
        display: inline-block;
        width: 12px;
        margin-right: 6px;
        color: rgba(0, 255, 0, 0.6);
        transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      #versionNotesWindow .expand-indicator.expanded {
        transform: rotate(90deg);
      }
      
      #versionNotesWindow .commit-date {
        font-size: 9px;
        color: rgba(0, 255, 0, 0.6);
        margin-bottom: 4px;
      }
      
      #versionNotesWindow .commit-message {
        font-size: 11px;
        color: #0f0;
        margin-bottom: 4px;
        line-height: 1.4;
      }
      
      #versionNotesWindow .commit-hash {
        font-size: 9px;
        color: rgba(0, 255, 0, 0.5);
        font-family: 'Berkeley Mono', 'Courier New', monospace;
      }
      
      #versionNotesWindow .commit-type {
        display: inline-block;
        padding: 1px 6px;
        margin-right: 6px;
        font-size: 9px;
        background: rgba(0, 255, 0, 0.2);
        border: 1px solid rgba(0, 255, 0, 0.4);
        border-radius: 3px;
        text-transform: uppercase;
      }
      
      #versionNotesWindow .commit-type.fix {
        background: rgba(255, 255, 0, 0.2);
        border-color: rgba(255, 255, 0, 0.4);
        color: #ff0;
      }
      
      #versionNotesWindow .commit-type.feat {
        background: rgba(0, 255, 255, 0.2);
        border-color: rgba(0, 255, 255, 0.4);
        color: #0ff;
      }
      
      #versionNotesWindow .contentDiv::-webkit-scrollbar {
        width: 8px;
      }
      
      #versionNotesWindow .contentDiv::-webkit-scrollbar-track {
        background: rgba(0, 255, 0, 0.1);
      }
      
      #versionNotesWindow .contentDiv::-webkit-scrollbar-thumb {
        background: rgba(0, 255, 0, 0.3);
        border-radius: 4px;
      }
      
      #versionNotesWindow .contentDiv::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 255, 0, 0.5);
      }
      
      #versionNotesWindow .copy-button {
        display: inline-block;
        margin-top: 12px;
        padding: 6px 12px;
        background: transparent;
        border: 1px solid rgba(0, 255, 0, 0.5);
        color: rgba(0, 255, 0, 0.8);
        font-size: 10px;
        font-family: 'Berkeley Mono', 'Courier New', monospace;
        cursor: pointer;
        text-transform: uppercase;
        transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      #versionNotesWindow .copy-button:hover {
        background: rgba(0, 255, 0, 0.1);
        border-color: #0f0;
        color: #0f0;
        box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
      }
      
      #versionNotesWindow .copy-button:active {
        transform: scale(0.95);
        box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
      }
      
      #versionNotesWindow .copy-button.copied {
        background: rgba(0, 255, 0, 0.2);
        border-color: #0f0;
        color: #0f0;
      }
    `;
    
    content.appendChild(style);
    content.appendChild(this.loadingDiv);
    content.appendChild(this.errorDiv);
    content.appendChild(this.contentDiv);
    
    return content;
  }
  
  async loadCommitHistory(forceRefresh = false) {
    // Show loading state
    this.showLoading();
    
    try {
      // For now, we'll use embedded commit data
      // In a real implementation, this could fetch from a server endpoint
      const commits = await this.fetchCommits();
      
      this.commits = commits;
      this.lastLoadTime = Date.now();
      this.displayCommits();
      
    } catch (error) {
      this.showError('Failed to load commit history: ' + error.message);
    }
  }
  
  async fetchCommits() {
    try {
      // Try to import the generated version notes
      const { VERSION_NOTES } = await import('../data/version-notes.js');
      return VERSION_NOTES;
    } catch (error) {
      console.warn('Generated version notes not found, using fallback data');
      // Fallback to some recent commits if the generated file doesn't exist
      return [
      {
        hash: '6369ca9',
        date: new Date('2025-01-29T10:30:00'),
        message: 'fix: Remove unnecessary safe area padding from stats window',
        author: 'Jeremy'
      },
      {
        hash: 'df5016d',
        date: new Date('2025-01-29T10:15:00'),
        message: 'fix: Adjust console/debug window positioning for iPad status bar',
        author: 'Jeremy'
      },
      {
        hash: 'feca76a',
        date: new Date('2025-01-29T10:00:00'),
        message: 'fix: Optimize PWA for iOS status bar limitations',
        author: 'Jeremy'
      },
      {
        hash: 'd34ee25',
        date: new Date('2025-01-29T09:45:00'),
        message: 'fix: Correct PWA start URL for iPad installation',
        author: 'Jeremy'
      },
      {
        hash: '271fc3b',
        date: new Date('2025-01-29T09:30:00'),
        message: 'fix: Resolve PWA service worker scope and icon issues',
        author: 'Jeremy'
      },
      {
        hash: '8e3a2f1',
        date: new Date('2025-01-28T18:00:00'),
        message: 'feat: Add configuration window with live game tuning 🎮',
        author: 'Jeremy'
      },
      {
        hash: '4c9d8e7',
        date: new Date('2025-01-28T16:30:00'),
        message: 'feat: Implement advanced windowing system with z-index management',
        author: 'Jeremy'
      },
      {
        hash: '2b5f7a9',
        date: new Date('2025-01-28T14:00:00'),
        message: 'feat: Add in-game console log window for mobile debugging 🐛',
        author: 'Jeremy'
      },
      {
        hash: '9d1c4e6',
        date: new Date('2025-01-27T20:00:00'),
        message: 'feat: Convert all UI to Berkeley Mono font',
        author: 'Jeremy'
      },
      {
        hash: '5a8b3c2',
        date: new Date('2025-01-27T17:30:00'),
        message: 'fix: Dynamic lighting and emissive materials in 3D renderer',
        author: 'Jeremy'
      },
      {
        hash: '7f2e9d1',
        date: new Date('2025-01-27T15:00:00'),
        message: 'feat: Add explosion effects for missiles and projectiles 💥',
        author: 'Jeremy'
      },
      {
        hash: '3c6a8e5',
        date: new Date('2025-01-26T22:00:00'),
        message: 'feat: Implement cooperative nebula gathering system',
        author: 'Jeremy'
      },
      {
        hash: '1e9f7b3',
        date: new Date('2025-01-26T18:00:00'),
        message: 'feat: Add crystal cores that grant instant unit spawns 💎',
        author: 'Jeremy'
      },
      {
        hash: '8a5c2d7',
        date: new Date('2025-01-25T16:00:00'),
        message: 'feat: Implement organics-based spawning economy',
        author: 'Jeremy'
      },
      {
        hash: '6b4e9f1',
        date: new Date('2025-01-25T12:00:00'),
        message: 'fix: Minification-safe resource type checking',
        author: 'Jeremy'
      },
      {
        hash: '4d7a3c8',
        date: new Date('2025-01-24T20:00:00'),
        message: 'feat: Add berserker mode for max-level soldiers 🗡️',
        author: 'Jeremy'
      },
      {
        hash: '2f8e6b9',
        date: new Date('2025-01-24T15:00:00'),
        message: 'feat: Implement state machine architecture with Robot3',
        author: 'Jeremy'
      },
      {
        hash: '9c3d5a7',
        date: new Date('2025-01-23T18:00:00'),
        message: 'feat: Add dual 2D/3D renderer system with R key toggle',
        author: 'Jeremy'
      },
      {
        hash: '5e7b2f4',
        date: new Date('2025-01-23T12:00:00'),
        message: 'feat: Implement spatial hashing collision system',
        author: 'Jeremy'
      },
      {
        hash: '1a9c8d6',
        date: new Date('2025-01-22T16:00:00'),
        message: 'feat: Add corpse looting mechanic for instant level-ups 💀',
        author: 'Jeremy'
      }
      ];
    }
  }
  
  displayCommits() {
    this.contentDiv.innerHTML = '';
    
    if (this.commits.length === 0) {
      this.contentDiv.innerHTML = `
        <div style="padding: 20px; text-align: center; color: rgba(0, 255, 0, 0.6);">
          No commits found
        </div>
      `;
      return;
    }
    
    this.commits.forEach((commit, index) => {
      const commitEl = document.createElement('div');
      commitEl.className = 'commit-item';
      
      // Determine commit type from message
      let commitType = '';
      let typeClass = '';
      if (commit.message.startsWith('fix:')) {
        commitType = 'FIX';
        typeClass = 'fix';
      } else if (commit.message.startsWith('feat:')) {
        commitType = 'FEAT';
        typeClass = 'feat';
      } else if (commit.message.startsWith('chore:')) {
        commitType = 'CHORE';
      } else if (commit.message.startsWith('docs:')) {
        commitType = 'DOCS';
      }
      
      // Format date
      const dateStr = this.formatDate(commit.date);
      
      // Clean message (remove type prefix)
      const cleanMessage = commit.message.replace(/^(fix|feat|chore|docs):\s*/i, '');
      
      // Check if this commit is expanded
      const isExpanded = index === this.expandedCommitIndex;
      
      // Create header (always visible)
      const headerDiv = document.createElement('div');
      headerDiv.className = 'commit-header' + (isExpanded ? ' expanded' : '');
      headerDiv.innerHTML = `
        <div class="commit-date">
          <span class="expand-indicator${isExpanded ? ' expanded' : ''}">▶</span>
          ${dateStr}
        </div>
        <div class="commit-message">
          ${commitType ? `<span class="commit-type ${typeClass}">${commitType}</span>` : ''}
          ${this.escapeHtml(cleanMessage)}
        </div>
        <div class="commit-hash">
          <span style="color: rgba(0, 255, 0, 0.4);">commit:</span> ${commit.hash}
          ${commit.author ? `<span style="color: rgba(0, 255, 0, 0.4); margin-left: 10px;">by:</span> ${commit.author}` : ''}
        </div>
      `;
      
      // Create details (collapsible)
      const detailsDiv = document.createElement('div');
      detailsDiv.className = 'commit-details' + (isExpanded ? ' expanded' : '');
      
      // Generate full commit details
      const fullMessage = this.generateFullCommitMessage(commit);
      detailsDiv.innerHTML = `
        <div class="commit-details-content">
          ${fullMessage}
        </div>
      `;
      
      // Add click handler to header
      headerDiv.addEventListener('click', () => {
        // If clicking on already expanded item, collapse it
        if (this.expandedCommitIndex === index) {
          this.expandedCommitIndex = -1;
        } else {
          this.expandedCommitIndex = index;
        }
        this.displayCommits(); // Re-render
        
        // Scroll the clicked item to the top after re-render with smooth animation
        if (this.expandedCommitIndex === index) {
          setTimeout(() => {
            const expandedHeader = this.contentDiv.querySelectorAll('.commit-header')[index];
            if (expandedHeader) {
              // Calculate target scroll position
              const targetScroll = expandedHeader.offsetTop - this.contentDiv.offsetTop;
              
              // Smooth scroll animation
              this.contentDiv.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
              });
            }
          }, 50); // Small delay to ensure DOM has updated
        }
      });
      
      commitEl.appendChild(headerDiv);
      commitEl.appendChild(detailsDiv);
      this.contentDiv.appendChild(commitEl);
    });
    
    this.hideLoading();
  }
  
  generateFullCommitMessage(commit) {
    // If we have a full message from git, use it
    if (commit.fullMessage) {
      // The fullMessage already has actual newlines from the data file, no need to unescape
      const message = commit.fullMessage;
      
      // Escape HTML but preserve newlines for white-space: pre-wrap
      const safeMessage = message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      
      // Generate a unique ID for this commit's copy button
      const buttonId = `copy-btn-${commit.hash}`;
      
      // Format it nicely
      let fullMessage = `<div style="white-space: pre-wrap; font-family: 'Berkeley Mono', monospace; font-size: 11px; line-height: 1.5; color: #0f0;">`;
      fullMessage += safeMessage;
      fullMessage += `</div>`;
      fullMessage += `<button class="copy-button" id="${buttonId}" data-commit-hash="${commit.hash}">Copy Commit Info</button>`;
      
      // Add click handler after a small delay to ensure DOM is ready
      setTimeout(() => {
        const button = document.getElementById(buttonId);
        if (button) {
          button.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent collapsing the commit
            this.copyCommitToClipboard(commit, button);
          });
        }
      }, 100);
      
      return fullMessage;
    }
    
    // Fallback: parse from the short message
    const lines = commit.message.split('\n').filter(line => line.trim());
    const mainMessage = lines[0];
    const details = lines.slice(1).join('\n');
    
    // Clean up the main message
    const cleanMain = mainMessage.replace(/^(fix|feat|chore|docs):\s*/i, '');
    
    // Generate a more detailed view
    let fullMessage = `<div style="margin-bottom: 12px;"><strong>CHANGES:</strong></div>`;
    
    // Add main change
    fullMessage += `<div style="margin-bottom: 8px;">• ${this.escapeHtml(cleanMain)}</div>`;
    
    // Add any additional details from the commit
    if (details) {
      fullMessage += `<div style="margin-top: 12px; white-space: pre-wrap;">${this.escapeHtml(details)}</div>`;
    }
    
    
    return fullMessage;
  }
  
  formatDate(date) {
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    let relative = '';
    if (days > 0) {
      relative = `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      relative = `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      relative = `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      relative = 'just now';
    }
    
    // Format date
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${dateStr} ${timeStr} • ${relative}`;
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  showLoading() {
    if (this.loadingDiv) this.loadingDiv.style.display = 'block';
    if (this.errorDiv) this.errorDiv.style.display = 'none';
    if (this.contentDiv) this.contentDiv.style.display = 'none';
  }
  
  hideLoading() {
    if (this.loadingDiv) this.loadingDiv.style.display = 'none';
    if (this.errorDiv) this.errorDiv.style.display = 'none';
    if (this.contentDiv) this.contentDiv.style.display = 'block';
  }
  
  showError(message) {
    if (this.loadingDiv) this.loadingDiv.style.display = 'none';
    if (this.contentDiv) this.contentDiv.style.display = 'none';
    if (this.errorDiv) {
      this.errorDiv.style.display = 'block';
      this.errorDiv.innerHTML = `
        <div>ERROR</div>
        <div style="margin-top: 10px; font-size: 10px;">${this.escapeHtml(message)}</div>
      `;
    }
  }
  
  refreshIfNeeded() {
    // Refresh if data is older than 5 minutes
    const fiveMinutes = 5 * 60 * 1000;
    if (Date.now() - this.lastLoadTime > fiveMinutes) {
      this.loadCommitHistory();
    }
  }
  
  copyCommitToClipboard(commit, button) {
    // Format the commit info for sharing
    const dateStr = commit.date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    let textToCopy = `Commit: ${commit.hash}\n`;
    textToCopy += `Date: ${dateStr}\n`;
    textToCopy += `Author: ${commit.author}\n\n`;
    textToCopy += commit.fullMessage || commit.message;
    
    // Copy to clipboard
    navigator.clipboard.writeText(textToCopy).then(() => {
      // Show success feedback
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.classList.add('copied');
      
      // Reset after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('copied');
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        button.textContent = 'Copied!';
        button.classList.add('copied');
        setTimeout(() => {
          button.textContent = 'Copy Commit Info';
          button.classList.remove('copied');
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    });
  }
}

// Create singleton instance
export const versionNotesWindow = new VersionNotesWindow();

// Make it globally accessible for debugging
window.versionNotesWindow = versionNotesWindow;